import type { AppStore } from "@/store/AppStore";
import {
  AiToolkitCollectionNotFoundError,
  AiToolkitNoteNotFoundError,
} from "@/modules/ai-toolkit/errors";
import type {
  AddNoteToCollectionInput,
  AddNoteToCollectionOutput,
} from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  addNoteToCollectionInputSchema,
  addNoteToCollectionOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import { EventContext } from "@/domains/metrics/context";

export const addNoteToCollection = async ({
  store,
  input,
}: {
  store: AppStore;
  input: AddNoteToCollectionInput;
}): Promise<AddNoteToCollectionOutput> => {
  const [note, collection] = await Promise.all([
    store.notes.getAsync(input.noteId),
    store.collections.getAsync(input.collectionId),
  ]);

  if (!note?.isAvailable) {
    throw new AiToolkitNoteNotFoundError({ noteId: input.noteId });
  }
  if (!collection) {
    throw new AiToolkitCollectionNotFoundError({ collectionId: input.collectionId });
  }

  const alreadyInCollection = await collection.itemList.hasItemAsync({ itemId: input.noteId });

  if (!alreadyInCollection) {
    await collection.itemList.addItem({
      itemId: input.noteId,
      itemKind: "NOTE",
      eventContext: EventContext.SidePanelSmartActions,
    });
  }

  return {
    noteId: input.noteId,
    collectionId: input.collectionId,
    added: !alreadyInCollection,
    alreadyInCollection,
  };
};

export const addNoteToCollectionTool: AiToolkitToolDefinition<
  AiToolkitToolName.AddNoteToCollection,
  AddNoteToCollectionInput,
  AddNoteToCollectionOutput
> = {
  name: AiToolkitToolName.AddNoteToCollection,
  inputSchema: addNoteToCollectionInputSchema,
  outputSchema: addNoteToCollectionOutputSchema,
  strict: true,
  execute: addNoteToCollection,
};
